import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Tag,
  Tabs,
  Spin,
  Form,
  Input,
  Button,
  Statistic,
  Timeline,
  Progress,
  Select,
  Empty,
  Divider,
  Modal,
  InputNumber,
  App,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  AimOutlined,
  TrophyOutlined,
  CalendarOutlined,
  DollarOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import apiService from '../../services/apiService';
import useSettingsStore from '../../store/settingsStore';

const { Title, Text } = Typography;

const getCurrentFiscalQuarter = () => {
  const currentMonth = new Date().getMonth() + 1;
  if (currentMonth >= 4 && currentMonth <= 6) return 'Q1';
  if (currentMonth >= 7 && currentMonth <= 9) return 'Q2';
  if (currentMonth >= 10 && currentMonth <= 12) return 'Q3';
  return 'Q4';
};

const AdminUserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const formatCurrency = useSettingsStore((s) => s.formatCurrency);
  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('target');
  const [targetHierarchy, setTargetHierarchy] = useState({ annual: null, quarters: [], months: [] });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentFiscalQuarter());
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetModalVisible, setTargetModalVisible] = useState(false);
  const [creatingTarget, setCreatingTarget] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [targetForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchTargetHierarchy();
    }
  }, [id]);

  useEffect(() => {
    fetchTargetHierarchy();
  }, [selectedYear]);

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get(`/admin/users/${id}`);
      if (response.data && response.data.data) {
        setUser(response.data.data);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to fetch user details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTargetHierarchy = async () => {
    setTargetLoading(true);
    try {
      const response = await apiService.get(`/targets/user/${id}/hierarchy/${selectedYear}`);
      console.log('Target hierarchy response:', response.data);
      if (response.data?.success && response.data.data) {
        console.log('Setting target hierarchy:', response.data.data);
        const hierarchyData = response.data.data;
        console.log('Annual target:', hierarchyData.annual);
        console.log('Quarters:', hierarchyData.quarters);
        console.log('Months:', hierarchyData.months);
        setTargetHierarchy(hierarchyData);
      } else if (response.data?.data) {
        // Handle case where success flag might not be present
        console.log('Setting target hierarchy (no success flag):', response.data.data);
        setTargetHierarchy(response.data.data);
      } else {
        console.log('No data in response, setting empty hierarchy');
        setTargetHierarchy({ annual: null, quarters: [], months: [] });
      }
    } catch (error) {
      console.error('Error fetching target hierarchy:', error);
      message.error('Failed to fetch targets');
      setTargetHierarchy({ annual: null, quarters: [], months: [] });
    } finally {
      setTargetLoading(false);
    }
  };

  const handleCreateTarget = async (values) => {
    if (creatingTarget) return; // double-click guard
    setCreatingTarget(true);

    // Helper that swallows 409s from auto-distributed child targets, so the
    // cascade doesn't abort partway through if a child already exists.
    const postIgnoreConflict = async (body) => {
      try {
        await apiService.post('/targets', body);
        return { ok: true };
      } catch (err) {
        if (err.response?.status === 409) {
          return { ok: false, conflict: true };
        }
        throw err;
      }
    };

    try {
      const payload = {
        ...values,
        year: selectedYear,
        targetAmount: Number(values.targetAmount),
        userId: id,
      };

      if (values.type === 'annual') {
        delete payload.period;

        // The top-level annual target is the user's intent — let any error
        // here (including 409 "already exists") surface clearly.
        await apiService.post('/targets', payload);
        message.success('Annual target created');

        let conflicts = 0;
        const quarterlyAmount = Number(values.targetAmount) / 4;
        const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
        const quarterMonthsMap = {
          Q1: ['04', '05', '06'],
          Q2: ['07', '08', '09'],
          Q3: ['10', '11', '12'],
          Q4: ['01', '02', '03'],
        };

        for (const quarter of quarters) {
          const qRes = await postIgnoreConflict({
            type: 'quarterly',
            period: quarter,
            year: selectedYear,
            targetAmount: quarterlyAmount,
            userId: id,
            currency: values.currency || 'INR',
            description: `Auto-distributed from annual target - ${quarter}`,
          });
          if (qRes.conflict) conflicts++;

          const monthlyAmount = quarterlyAmount / 3;
          for (const month of quarterMonthsMap[quarter]) {
            const mRes = await postIgnoreConflict({
              type: 'monthly',
              period: month,
              year: selectedYear,
              targetAmount: monthlyAmount,
              userId: id,
              currency: values.currency || 'INR',
              description: `Auto-distributed from ${quarter} - Month ${month}`,
            });
            if (mRes.conflict) conflicts++;
          }
        }

        if (conflicts > 0) {
          message.warning(`${conflicts} child target(s) already existed and were left as-is.`);
        } else {
          message.success('Quarterly and monthly targets auto-distributed');
        }
      } else if (values.type === 'quarterly') {
        await apiService.post('/targets', payload);
        message.success('Quarterly target created');

        let conflicts = 0;
        const monthlyAmount = Number(values.targetAmount) / 3;
        const quarterMonthsMap = {
          Q1: ['04', '05', '06'],
          Q2: ['07', '08', '09'],
          Q3: ['10', '11', '12'],
          Q4: ['01', '02', '03'],
        };
        for (const month of quarterMonthsMap[values.period] || []) {
          const r = await postIgnoreConflict({
            type: 'monthly',
            period: month,
            year: selectedYear,
            targetAmount: monthlyAmount,
            userId: id,
            currency: values.currency || 'INR',
            description: `Auto-distributed from ${values.period} - Month ${month}`,
          });
          if (r.conflict) conflicts++;
        }
        if (conflicts > 0) {
          message.warning(`${conflicts} monthly target(s) already existed.`);
        } else {
          message.success('Monthly targets auto-distributed (33.33% each)');
        }
      } else {
        await apiService.post('/targets', payload);
        message.success('Target created successfully');
      }

      setTargetModalVisible(false);
      targetForm.resetFields();
      setTimeout(() => fetchTargetHierarchy(), 500);
    } catch (error) {
      const status = error.response?.status;
      const upstreamMsg = error.response?.data?.message;
      if (status === 409) {
        message.error(upstreamMsg || 'A target already exists for this period.', 6);
      } else {
        message.error(upstreamMsg || 'Failed to create target');
      }
    } finally {
      setCreatingTarget(false);
    }
  };

  const handleUpdateTarget = async (values) => {
    if (!editingTarget?._id) {
      message.error('Invalid target selected');
      return;
    }

    try {
      await apiService.put(`/targets/${editingTarget._id}`, {
        ...values,
        targetAmount: Number(values.targetAmount),
      });
      message.success('Target updated successfully');
      setTargetModalVisible(false);
      setEditingTarget(null);
      targetForm.resetFields();
      // Add small delay to ensure database is updated
      setTimeout(() => {
        fetchTargetHierarchy();
      }, 500);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update target');
    }
  };

  const openCreateModal = (type, period = null) => {
    setEditingTarget(null);
    targetForm.resetFields();
    targetForm.setFieldsValue({ type, period });
    setTargetModalVisible(true);
  };

  const openEditModal = (target) => {
    setEditingTarget(target);
    targetForm.setFieldsValue({
      type: target.type,
      period: target.period,
      targetAmount: target.targetAmount,
      currency: target.currency,
      description: target.description,
    });
    setTargetModalVisible(true);
  };

  const handleDeleteTarget = (target) => {
    const isAnnual = target.type === 'annual';
    const isQuarterly = target.type === 'quarterly';
    
    let deleteMessage = `Are you sure you want to delete this ${target.type} target?`;
    if (isAnnual) {
      deleteMessage = `Are you sure you want to delete this Annual target? This will also delete all auto-distributed quarterly and monthly targets.`;
    } else if (isQuarterly) {
      deleteMessage = `Are you sure you want to delete this ${target.period} target? This will also delete all auto-distributed monthly targets for this quarter.`;
    }

    Modal.confirm({
      title: 'Delete Target',
      content: deleteMessage,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          // If deleting annual target, also delete all quarterly and monthly targets
          if (isAnnual) {
            // Delete all quarterly targets first
            if (targetHierarchy.quarters && targetHierarchy.quarters.length > 0) {
              for (const quarter of targetHierarchy.quarters) {
                await apiService.delete(`/targets/${quarter._id}`);
              }
            }
            // Delete all monthly targets
            if (targetHierarchy.months && targetHierarchy.months.length > 0) {
              for (const month of targetHierarchy.months) {
                await apiService.delete(`/targets/${month._id}`);
              }
            }
          } 
          // If deleting quarterly target, also delete related monthly targets
          else if (isQuarterly) {
            const quarterMonthsMap = {
              'Q1': ['04', '05', '06'],
              'Q2': ['07', '08', '09'],
              'Q3': ['10', '11', '12'],
              'Q4': ['01', '02', '03']
            };
            const monthsToDelete = quarterMonthsMap[target.period] || [];
            
            if (targetHierarchy.months && targetHierarchy.months.length > 0) {
              for (const month of targetHierarchy.months) {
                if (monthsToDelete.includes(month.period)) {
                  await apiService.delete(`/targets/${month._id}`);
                }
              }
            }
          }
          
          // Delete the main target
          await apiService.delete(`/targets/${target._id}`);
          
          let successMessage = 'Target deleted successfully';
          if (isAnnual) {
            successMessage = 'Annual target and all quarterly/monthly targets deleted successfully';
          } else if (isQuarterly) {
            successMessage = `${target.period} target and its monthly targets deleted successfully`;
          }
          message.success(successMessage);
          
          // Add small delay to ensure database is updated
          setTimeout(() => {
            fetchTargetHierarchy();
          }, 500);
        } catch (error) {
          message.error('Failed to delete target');
        }
      },
    });
  };

  const handlePasswordChange = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('New password and confirm password do not match');
      return;
    }

    try {
      await apiService.post(`/admin/users/${id}/reset-password`, {
        newPassword: values.newPassword,
      });
      message.success('Password updated successfully');
      passwordForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update password');
    }
  };

  const renderHeader = () => {
    if (!user) {
      return null;
    }

    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';

    return (
      <Card className="shadow-soft h-full bg-white dark:bg-gray-800">
        <div className="text-center mb-4">
          <Avatar
            size={80}
            icon={<UserOutlined />}
            src={user.avatar}
            className="mb-3"
          />
          <Title level={4} className="mb-1">
            {fullName}
          </Title>
        </div>

        <div className="space-y-3">
          <div className="mb-3">
            <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
              Email:
            </Text>
            <div className="flex items-center">
              <MailOutlined className="mr-2 text-gray-400" />
              <a href={`mailto:${user.email}`} className="text-blue-600 break-all">
                {user.email}
              </a>
            </div>
          </div>

          {user.phone && (
            <div className="mb-3">
              <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
                Phone:
              </Text>
              <div className="flex items-center">
                <PhoneOutlined className="mr-2 text-gray-400" />
                <a href={`tel:${user.phone}`} className="text-blue-600">
                  {user.phone}
                </a>
              </div>
            </div>
          )}

          <div className="mb-3">
            <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
              Role:
            </Text>
            <Tag color={user.role === 'tenant_admin' ? 'orange' : 'blue'} className="capitalize">
              {user.role?.replace('_', ' ')}
            </Tag>
          </div>

          <div className="mb-3">
            <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
              Status:
            </Text>
            <Tag color={user.isActive ? 'green' : 'red'}>
              {user.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </div>

          {user.lastLogin && (
            <div className="mb-3">
              <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
                Last Login:
              </Text>
              <Text className="text-sm">
                {new Date(user.lastLogin).toLocaleString()}
              </Text>
            </div>
          )}

          <div className="mb-3">
            <Text strong className="block mb-1 text-gray-600 dark:text-gray-400">
              Created:
            </Text>
            <Text className="text-sm">
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
            </Text>
          </div>
        </div>
      </Card>
    );
  };

  const renderActivityTimeline = () => {
    if (!user) {
      return null;
    }

    const items = [
      {
        dot: <UserOutlined className="text-green-500" />,
        children: (
          <div>
            <Text strong>User Created</Text>
            <br />
            <Text type="secondary" className="text-sm">
              {user.createdAt
                ? new Date(user.createdAt).toLocaleString()
                : 'Unknown date'}
            </Text>
            <br />
            <Text className="text-sm">User account was created</Text>
          </div>
        ),
      },
    ];

    if (user.lastLogin) {
      items.push({
        dot: <MailOutlined className="text-blue-500" />,
        children: (
          <div>
            <Text strong>Last Login</Text>
            <br />
            <Text type="secondary" className="text-sm">
              {new Date(user.lastLogin).toLocaleString()}
            </Text>
            <br />
            <Text className="text-sm">Most recent login activity</Text>
          </div>
        ),
      });
    }

    items.push({
      dot: <PhoneOutlined className="text-orange-500" />,
      children: (
        <div>
          <Text strong>Status</Text>
          <br />
          <Text type="secondary" className="text-sm">
            {user.isActive ? 'Active' : 'Inactive'}
          </Text>
          <br />
          <Text className="text-sm">
            Current user status and role: {user.role?.replace('_', ' ')}
          </Text>
        </div>
      ),
    });

    return (
      <Card
        className="shadow-soft h-full bg-white dark:bg-gray-800"
        title="Activity Timeline"
      >
        <Timeline items={items} />
      </Card>
    );
  };

  const renderTargetTab = () => {
    const getProgressColor = (percentage) => {
      if (percentage < 50) return '#ff9900';
      if (percentage < 80) return '#87d068';
      return '#52c41a';
    };

    const getMonthLabel = (month) => {
      const monthIndex = parseInt(month, 10) - 1;
      if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return month;
      return new Date(2024, monthIndex, 1).toLocaleString('default', { month: 'short' });
    };

    const renderTargetCard = (target, icon, label, options = {}) => {
      const { type: explicitType, period: explicitPeriod } = options;

      if (!target) {
        const type = explicitType || (label.includes('Q') ? 'quarterly' : label.includes('M') ? 'monthly' : 'annual');
        const isQuarterlyOrMonthly = type === 'quarterly' || type === 'monthly';
        const isAnnual = type === 'annual';
        const hasQuarterlyTargets = targetHierarchy.quarters && targetHierarchy.quarters.length > 0;
        
        // Disable quarterly/monthly if annual exists
        const isDisabledQuarterlyMonthly = isQuarterlyOrMonthly && targetHierarchy.annual;
        // Disable annual if quarterly targets exist
        const isDisabledAnnual = isAnnual && hasQuarterlyTargets;
        const isDisabled = isDisabledQuarterlyMonthly || isDisabledAnnual;
        
        let disabledMessage = '';
        if (isDisabledQuarterlyMonthly) {
          disabledMessage = 'Quarterly/Monthly targets are auto-distributed from Annual target';
        } else if (isDisabledAnnual) {
          disabledMessage = 'Delete all quarterly targets first to create Annual target';
        }
        
        return (
          <Card className="shadow-soft bg-gray-50 dark:bg-gray-700" size="small">
            <div className="flex items-center justify-between">
              <Text type="secondary" className="text-sm">
                {label} - {isDisabled ? (isDisabledAnnual ? 'Locked' : 'Auto-distributed') : 'Not Set'}
              </Text>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                disabled={isDisabled}
                title={disabledMessage}
                onClick={() => {
                  const type = explicitType || (label.includes('Q') ? 'quarterly' : label.includes('M') ? 'monthly' : 'annual');
                  const period = explicitPeriod !== undefined
                    ? explicitPeriod
                    : label.includes('Q')
                      ? label
                      : label.includes('M')
                        ? label.replace('M', '')
                        : null;
                  openCreateModal(type, period);
                }}
              />
            </div>
          </Card>
        );
      }

      const percentage = target.targetAmount > 0 
        ? Number(((target.achievedAmount / target.targetAmount) * 100).toFixed(2))
        : 0;

      // Check if this is an auto-distributed quarterly/monthly target
      const isAutoDistributed = targetHierarchy.annual && (target.type === 'quarterly' || target.type === 'monthly');
      const canDelete = !isAutoDistributed;
      
      // Check if annual target is locked (when quarterly targets exist) - locked for editing only
      const hasQuarterlyTargets = targetHierarchy.quarters && targetHierarchy.quarters.length > 0;
      const isAnnualLocked = target.type === 'annual' && hasQuarterlyTargets;

      return (
        <Card className="shadow-soft" size="small">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {icon}
              <Text strong className="text-sm">{label}</Text>
              {isAutoDistributed && target.type === 'quarterly' && (
                <Tag color="blue" className="text-xs">Auto-distributed</Tag>
              )}
              {isAnnualLocked && <Tag color="orange" className="text-xs">Edit Locked</Tag>}
            </div>
            <div className="flex items-center space-x-1">
              <Tag color={percentage >= 75 ? 'green' : percentage >= 50 ? 'orange' : 'red'}>
                {percentage}%
              </Tag>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                disabled={isAnnualLocked}
                title={isAnnualLocked ? 'Annual target is locked for editing. Delete to modify.' : ''}
                onClick={() => openEditModal(target)}
              />
            </div>
          </div>
          <Divider className="my-2" />
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <Text type="secondary">Target:</Text>
              <Text strong>{formatCurrency(target.targetAmount)}</Text>
            </div>
            <div className="flex justify-between">
              <Text type="secondary">Achieved:</Text>
              <Text strong className="text-green-600">{formatCurrency(target.achievedAmount)}</Text>
            </div>
            <div className="flex justify-between">
              <Text type="secondary">Remaining:</Text>
              <Text strong className="text-orange-600">
                {formatCurrency((target.targetAmount || 0) - (target.achievedAmount || 0))}
              </Text>
            </div>
          </div>
          <Progress 
            percent={percentage} 
            strokeColor={getProgressColor(percentage)}
            size="small"
            className="mt-2"
          />
        </Card>
      );
    };

    return (
      <div
        className="space-y-4"
        style={{
          height: "550px",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <TrophyOutlined className="text-blue-500" />
            <Text strong>Sales Targets</Text>
          </div>
          <div className="flex items-center space-x-2">
            <Select
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              style={{ width: 100 }}
              size="small"
            >
              {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => (
                <Select.Option key={quarter} value={quarter}>{quarter}</Select.Option>
              ))}
            </Select>
            <Select
              value={selectedYear}
              onChange={(year) => {
                setSelectedYear(year);
                setSelectedQuarter(getCurrentFiscalQuarter());
              }}
              style={{ width: 100 }}
              size="small"
            >
              {[2023, 2024, 2025, 2026].map(year => (
                <Select.Option key={year} value={year}>{year}</Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {targetLoading ? (
          <div className="text-center py-8">
            <Spin size="small" />
            <Text className="block mt-2 text-gray-500 text-sm">Loading targets...</Text>
          </div>
        ) : !targetHierarchy.annual && (!targetHierarchy.quarters || targetHierarchy.quarters.length === 0) && (!targetHierarchy.months || targetHierarchy.months.length === 0) ? (
          <div className="text-center py-8">
            <Empty description="No targets set for this year" />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('annual')}
              style={{ marginTop: '16px' }}
            >
              Create Target
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Text strong className="text-sm">Year & Quarter Targets</Text>
                <div className="flex items-center space-x-2">
                  <Tag color="gold" className="text-xs">{selectedYear}</Tag>
                  <Tag color="blue" className="text-xs">{selectedQuarter}</Tag>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
                    {renderTargetCard(
                      targetHierarchy.annual,
                      <TrophyOutlined className="text-yellow-500" />,
                      'Annual Target',
                      { type: 'annual', period: null }
                    )}
                  </div>
                </div>

                <div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2">
                    {renderTargetCard(
                      targetHierarchy.quarters?.find(q => q.period === selectedQuarter),
                      <CalendarOutlined className="text-blue-500" />,
                      selectedQuarter,
                      { type: 'quarterly', period: selectedQuarter }
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              {(() => {
                // Get current quarter and its months (Fiscal year: April-March)
                const currentQuarter = selectedQuarter;
                const quarterMonthsMap = {
                  'Q1': ['04', '05', '06'],
                  'Q2': ['07', '08', '09'],
                  'Q3': ['10', '11', '12'],
                  'Q4': ['01', '02', '03'],
                };
                const quarterMonths = quarterMonthsMap[currentQuarter] || [];
                
                // Get the quarterly target for current quarter
                const currentQuarterTarget = targetHierarchy.quarters?.find(q => q.period === currentQuarter);
                
                return (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Text strong className="text-sm">Monthly Breakdown</Text>
                      <Tag color="blue" className="text-xs">{currentQuarter}</Tag>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {quarterMonths.map(month => {
                        const monthTarget = targetHierarchy.months?.find(m => m.period === month);
                        return (
                          <div key={month}>
                            {renderTargetCard(
                              monthTarget,
                              <CalendarOutlined className="text-green-500" />,
                              getMonthLabel(month),
                              { type: 'monthly', period: month }
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {currentQuarterTarget && !targetHierarchy.months?.some(m => quarterMonths.includes(m.period)) && (
                      <Button
                        type="dashed"
                        block
                        size="small"
                        onClick={() => {
                          const monthlyAmount = currentQuarterTarget.targetAmount / 3;
                          quarterMonths.forEach(month => {
                            apiService.post('/targets', {
                              type: 'monthly',
                              period: month,
                              year: selectedYear,
                              targetAmount: monthlyAmount,
                              userId: id,
                              currency: currentQuarterTarget.currency || 'INR',
                              description: `Auto-distributed from ${currentQuarter} - Month ${month}`,
                            }).catch(err => console.error(`Failed to create monthly target for ${month}:`, err));
                          });
                          message.success('Monthly targets auto-distributed (33.33% each)');
                          setTimeout(() => fetchTargetHierarchy(), 500);
                        }}
                        style={{ marginTop: '8px' }}
                      >
                        Auto-distribute {currentQuarter} to Months
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <Modal
          title={editingTarget ? 'Edit Target' : 'Create Target'}
          open={targetModalVisible}
          confirmLoading={creatingTarget}
          okButtonProps={{ disabled: creatingTarget }}
          cancelButtonProps={{ disabled: creatingTarget }}
          maskClosable={!creatingTarget}
          closable={!creatingTarget}
          onOk={() => {
            if (creatingTarget) return;
            targetForm.submit();
          }}
          onCancel={() => {
            if (creatingTarget) return;
            setTargetModalVisible(false);
            setEditingTarget(null);
            targetForm.resetFields();
          }}
          width={500}
        >
          <Form
            form={targetForm}
            layout="vertical"
            onFinish={editingTarget ? handleUpdateTarget : handleCreateTarget}
          >
            <Form.Item
              name="type"
              label="Target Type"
              rules={[{ required: true, message: 'Please select target type' }]}
            >
              <Select disabled={!!editingTarget}>
                <Select.Option 
                  value="annual" 
                  disabled={!editingTarget && targetHierarchy.quarters && targetHierarchy.quarters.length > 0}
                >
                  Annual {!editingTarget && targetHierarchy.quarters && targetHierarchy.quarters.length > 0 ? '(Delete quarterly targets first)' : ''}
                </Select.Option>
                <Select.Option value="quarterly" disabled={!!targetHierarchy.annual}>
                  Quarterly {targetHierarchy.annual ? '(Auto-distributed from Annual)' : ''}
                </Select.Option>
                <Select.Option value="monthly" disabled={!!targetHierarchy.annual}>
                  Monthly {targetHierarchy.annual ? '(Auto-distributed from Annual)' : ''}
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
            >
              {({ getFieldValue }) => {
                const type = getFieldValue('type');
                if (type === 'annual') return null;
                
                if (type === 'quarterly') {
                  return (
                    <Form.Item
                      name="period"
                      label="Quarter"
                      rules={[{ required: true, message: 'Please select quarter' }]}
                    >
                      <Select placeholder="Select quarter" disabled={!!editingTarget}>
                        <Select.Option value="Q1">Q1</Select.Option>
                        <Select.Option value="Q2">Q2</Select.Option>
                        <Select.Option value="Q3">Q3</Select.Option>
                        <Select.Option value="Q4">Q4</Select.Option>
                      </Select>
                    </Form.Item>
                  );
                }
                
                if (type === 'monthly') {
                  return (
                    <Form.Item
                      name="period"
                      label="Month"
                      rules={[{ required: true, message: 'Please select month' }]}
                    >
                      <Select placeholder="Select month" disabled={!!editingTarget}>
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(month => (
                          <Select.Option key={month} value={month}>
                            {new Date(2024, parseInt(month) - 1, 1).toLocaleString('default', { month: 'long' })}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }
                
                return null;
              }}
            </Form.Item>

            <Form.Item
              name="targetAmount"
              label="Target Amount"
              rules={[
                { required: true, message: 'Please enter target amount' },
                { type: 'number', min: 0, message: 'Amount must be greater than 0' },
              ]}
            >
              <InputNumber
                placeholder="0.00"
                min={0}
                step={100}
                formatter={(value) => `${currencySymbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => parseFloat(value.replace(new RegExp(`${currencySymbol}\\s?|(,*)`, 'g'), ''))}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <Input.TextArea rows={3} placeholder="Add notes about this target" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderPasswordTab = () => {
    return (
      <div
        className="space-y-4"
        style={{
          height: "550px",
          overflowY: "auto",
        }}
      >
        <Card
          title={
            <span className="flex items-center space-x-2">
              <LockOutlined />
              <span>Change Password</span>
            </span>
          }
          className="shadow-soft"
        >
          <Text className="block mb-4 text-gray-600 dark:text-gray-400">
            Set a new password for this user. The user will be required to log in again with
            the new password.
          </Text>
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordChange}
          >
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter new password' },
                { min: 8, message: 'Password must be at least 8 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message:
                    'Password must contain uppercase, lowercase, number and special character (@$!%*?&)',
                },
              ]}
              hasFeedback
            >
              <Input.Password className="focus-visible:focus" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["newPassword"]}
              hasFeedback
              rules={[
                { required: true, message: 'Please confirm password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password className="focus-visible:focus" />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button type="primary" htmlType="submit" className="focus-visible:focus">
                Update Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  };

  const tabItems = [
    {
      key: 'target',
      label: 'Target',
      children: renderTargetTab(),
    },
    {
      key: 'password',
      label: 'Password',
      children: renderPasswordTab(),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/users')}
          className="focus-visible:focus"
        >
          Back to Users
        </Button>
        {user && (
          <Statistic
            title="User Created"
            value={new Date(user.createdAt).toLocaleDateString()}
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={24} className="h-full">
          <Col xs={24} lg={5}>
            {renderHeader()}
          </Col>
          <Col xs={24} lg={14}>
            <Card className="shadow-soft h-full">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
              />
            </Card>
          </Col>
          <Col xs={24} lg={5}>
            {renderActivityTimeline()}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AdminUserDetailPage;
